import { IFilterInput, KeyValueObj } from "@/common/types";

// MED-713: config-driven validation for range filters (price, date, or any
// other lower/upper pair) so an inverted range can't be submitted from the
// filter modal. Two filter inputs form a range when they share the same
// `range.group`; the check only runs once BOTH bounds are filled in, so a
// one-sided range (a minimum with no maximum) stays valid.
//
// Confirmed rules (see ticket "Before starting"):
//  - Only one bound supplied  -> valid, not checked here.
//  - Equal lower and upper    -> valid (single-day date range, exact price).
//    Only a strictly inverted range (lower > upper) is rejected.
//  - Backend validation is deliberately out of scope; this is the interface
//    guard only.

type Compare = "number" | "date";

function toComparable(raw: unknown, compare: Compare): number | null {
  if (raw === undefined || raw === null || raw === "") return null;

  if (compare === "date") {
    const time = new Date(String(raw)).getTime();
    return Number.isNaN(time) ? null : time;
  }

  // Numbers may be typed with grouping separators ("1,000").
  const value = Number(String(raw).replace(/,/g, "").trim());
  return Number.isNaN(value) ? null : value;
}

/**
 * Returns a map of filter-input `name` -> error message for every range whose
 * lower bound exceeds its upper bound. The message is attached to the upper
 * bound (the field the user most likely needs to change), naming both fields so
 * it's clear which range is at fault. An empty object means every range is
 * valid (or no range metadata is declared, e.g. on screens that never opted in).
 */
export function validateRangeFilters(
  filterInputs: IFilterInput[] | undefined,
  formValues: KeyValueObj,
): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!filterInputs?.length) return errors;

  const groups = new Map<
    string,
    { lower?: IFilterInput; upper?: IFilterInput }
  >();

  filterInputs.forEach((input) => {
    if (!input.range) return;
    const group = groups.get(input.range.group) ?? {};
    group[input.range.bound] = input;
    groups.set(input.range.group, group);
  });

  groups.forEach(({ lower, upper }) => {
    if (!lower || !upper) return;

    const compare: Compare =
      lower.range?.compare ??
      (lower.type === "date" || upper.type === "date" ? "date" : "number");

    const lowerValue = toComparable(formValues[lower.name], compare);
    const upperValue = toComparable(formValues[upper.name], compare);

    // Both bounds must be present and parseable before we can compare them.
    if (lowerValue === null || upperValue === null) return;

    if (lowerValue > upperValue) {
      const relation = compare === "date" ? "earlier than" : "less than";
      errors[upper.name] =
        `${upper.label} cannot be ${relation} ${lower.label}`;
    }
  });

  return errors;
}
